import React, { useEffect, useState, useCallback } from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { getHrActivityLogs } from "../../api/api";

// ======================================================
// ACTION -> CHIP COLOR
// ======================================================

const actionColor = (action) => {
  if (action.includes("credit")) return "success";
  if (action.includes("debit")) return "error";
  if (action.includes("edit")) return "warning";
  return "default";
};

export default function ActivityLogs() {

  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getHrActivityLogs(search ? { q: search } : {});
      setLogs(Array.isArray(data?.activities) ? data.activities : []);    
    } catch (err) {
      console.error("Failed to load activity logs:", err);
    } finally {
      setLoading(false);
    }
  }, [search]);

  useEffect(() => {
    const timer = setTimeout(load, 300); // debounce search
    return () => clearTimeout(timer);
  }, [load]);

  return (
    <Box sx={{ p: 2 }}>
        <div style={{display: "flex", alignItems: "center", justifyContent: "space-between"}}>

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        Activity Logs
      </Typography>

      <TextField
        size="small"
        placeholder="Search by actor email, employee name, email, or emp code"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" sx={{ color: "text.secondary" }} />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2, width: 400, maxWidth: "100%", background: "#fff" }}
      />
      </div>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 700, background: "#f5f7fa" } }}>
                <TableCell>Date &amp; Time</TableCell>
                <TableCell>Performed By</TableCell>
                <TableCell>Action</TableCell>
                <TableCell>Target Employee</TableCell>
                <TableCell>Details</TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              {logs.map((log) => (
                <TableRow key={log.id} hover>
                  <TableCell>
                    <Typography variant="body2">
                      {new Date(log.created_at).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{log.actor_email}</Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={log.action?.replace(/_/g, " ")}
                      color={actionColor(log.action || "")}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    {log.target_user_name ? (
                      <>
                        <Typography variant="body2">{log.target_user_name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {log.target_user_email}
                        </Typography>
                      </>
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {log.details}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}

              {logs.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} align="center" sx={{ py: 4, color: "text.secondary" }}>
                    No activity found.
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}